import { useEffect, useState } from 'react';

const readStored = <T>(
  key: string,
  parse: (stored: unknown) => T | null
): T | null => {
  try {
    return parse(JSON.parse(window.localStorage.getItem(key) ?? 'null'));
  } catch {
    return null;
  }
};

const saveStored = <T>(key: string, value: T | null) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The value is still kept until the page reloads
  }
};

// State kept in local storage under `key`, so it survives a reload and is shared by the page's open tabs. It is read
// when the component mounts, so a component whose storage key can change also takes the storage key as its React key.
// `parse` returns null for a stored value it does not recognize.
export const useStoredValue = <T>(
  key: string,
  parse: (stored: unknown) => T | null
) => {
  const [value, setValue] = useState(() => readStored(key, parse));
  // Another tab saved a new value
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === key) setValue(readStored(key, parse));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key, parse]);
  const storeValue = (newValue: T | null) => {
    setValue(newValue);
    saveStored(key, newValue);
  };
  return [value, storeValue] as const;
};
